import { RSAA } from 'redux-api-middleware'

import { initialState, types } from './index'
import {
  createDoctorImageUrl,
  getApiPath,
  makeQueryParams,
} from 'sidecar-utility-library'

/**
 * This file contains the actions and reducers for the providers tab in the
 * provider detail page. We fetch related providers given an npi, name, gender,
 * language, gam score, and specialty.
 */

export const fetchPracticeLocationProviders = ({
  locationId,
  name,
  gender,
  language,
  quality,
  specialty,
}) => {
  return {
    [RSAA]: {
      endpoint: getApiPath(
        'doc',
        `doctors/${locationId}/location-doctors${makeQueryParams({
          name,
          gender,
          language,
          quality,
          specialty,
        })}`,
        'v2',
      ),
      method: 'GET',
      types: [
        types.FETCH_PRACTICE_LOCATION_PROVIDERS_REQUEST,
        types.FETCH_PRACTICE_LOCATION_PROVIDERS_SUCCESS,
        types.FETCH_PRACTICE_LOCATION_PROVIDERS_ERROR,
      ],
    },
  }
}

export const clearPracticeLocationProviders = () => {
  return {
    type: types.CLEAR_PRACTICE_LOCATION_PROVIDERS,
  }
}

export const updatePracticeLocationProviderBookmark = ({ npi, bookmarked }) => {
  return {
    type: types.UPDATE_PRACTICE_LOCATION_PROVIDER_BOOKMARK,
    payload: { npi, bookmarked },
  }
}

/**
 * Contact info for a member's Price Lock locations, from member-app's lookup:
 * practices-api, then doctor-service for what the index lacks (MX-5746,
 * MX-5748). A location neither has is left out of the result.
 *
 * @param {array} locationUuids - List of location uuids we need contact info
 *   for
 * @returns - Array of practice location objects that contain contact info
 */
export const fetchMultipleLocations = ({ locationUuids = [] }) => ({
  [RSAA]: {
    endpoint: `/api/care/locations?locationUuids=${locationUuids
      .map(encodeURIComponent)
      .join(',')}`,
    method: 'GET',
    types: [
      types.FETCH_LOCATION_CONTACT_INFO_REQUEST,
      types.FETCH_LOCATION_CONTACT_INFO_SUCCESS,
      types.FETCH_LOCATION_CONTACT_INFO_ERROR,
    ],
  },
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.FETCH_PRACTICE_LOCATION_PROVIDERS_REQUEST: {
      return {
        ...state,
        practiceLocationProviders: {
          ...state.practiceLocationProviders,
          busy: true,
          error: '',
        },
      }
    }
    case types.FETCH_PRACTICE_LOCATION_PROVIDERS_SUCCESS: {
      const {
        addresses,
        content,
        contractType,
        facilityName,
        facilityNpi,
        locationUuid,
      } = payload

      const updatedContent =
        content.length > 0
          ? content.map(provider => ({
              ...provider,
              imgUrl: createDoctorImageUrl(provider),
            }))
          : content

      return {
        ...state,
        practiceLocationProviders: {
          ...state.practiceLocationProviders,
          busy: false,
          error: '',
          entity: {
            providers: updatedContent,
            addresses,
            facilityName,
            facilityNpi,
            locationUuid,
            contractType,
          },
        },
      }
    }
    case types.FETCH_PRACTICE_LOCATION_PROVIDERS_ERROR: {
      return {
        ...state,
        practiceLocationProviders: {
          ...state.practiceLocationProviders,
          busy: false,
          error: payload,
        },
      }
    }

    case types.UPDATE_PRACTICE_LOCATION_PROVIDER_BOOKMARK: {
      return {
        ...state,
        practiceLocationProviders: {
          ...state.practiceLocationProviders,
          entity: {
            ...state?.practiceLocationProviders?.entity,
            providers: state?.practiceLocationProviders?.entity?.providers?.map(
              doctor =>
                doctor.npi != null && doctor.npi === payload.npi
                  ? { ...doctor, bookmarked: payload.bookmarked }
                  : doctor,
            ),
          },
        },
      }
    }

    case types.CLEAR_PRACTICE_LOCATION_PROVIDERS: {
      return {
        ...state,
        practiceLocationProviders: initialState.practiceLocationProviders,
      }
    }

    case types.FETCH_LOCATION_CONTACT_INFO_REQUEST:
      return {
        ...state,
        practiceLocations: {
          ...state.practiceLocations,
          busy: true,
        },
      }
    case types.FETCH_LOCATION_CONTACT_INFO_SUCCESS:
      return {
        ...state,
        practiceLocations: {
          ...state.practiceLocations,
          busy: false,
          entities: payload || [],
        },
      }
    case types.FETCH_LOCATION_CONTACT_INFO_ERROR:
      return {
        ...state,
        practiceLocations: {
          ...state.practiceLocations,
          busy: false,
          error: payload?.response?.message,
        },
      }

    default:
      return state
  }
}
